/**
 * DealPanel — painel de VISUALIZAÇÃO do Deal (card do kanban).
 *
 * Drawer lateral largo, 2 colunas, abas à direita — mesma visão usada dentro
 * do perfil do Cliente/Lead (ClienteView/LeadView abrem este componente na
 * aba "Negócios"). O corpo das abas reaproveita panelKit.jsx (HistoryTab,
 * ActivitiesTab, DealsTab, FilesTab, ItemsTab, ContractsTab,
 * ConfirmDialog) — os mesmos componentes que ClienteView/LeadView já usam,
 * sem duplicar UI. Só a casca (identidade, resumo, métricas, abas) segue
 * agora o design v2 (`cv-*`, clienteView.css) em vez do drawer antigo.
 *
 * NÃO substitui o DealModal: criar/editar continua no DealModal. Aqui é só
 * leitura + ações (Editar → abre o DealModal; Excluir → soft delete).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import {
  getDeal,
  getDealTransitions,
  updateDeal,
  deleteDeal,
  moveDeal,
  unlinkDealContact,
  getDealContacts,
  getDealActivities,
  createDealActivity,
  getCrmContactDeals,
  getCrmMembers,
  getPipelines,
  getStages,
  getCompany,
  fmtBRL,
} from '../../db/api.js'
import {
  DEAL_STATUS_COLORS, fmtDate, fmtDateTime,
  ActivitiesTab, DealsTab, ItemsTab, ContractsTab, FilesTab, ConfirmDialog,
} from './panelKit.jsx'
import AgendaEventModal from '../agenda/AgendaEventModal.jsx'
import LossReasonModal from './LossReasonModal.jsx'
import DealWinFlow from './DealWinFlow.jsx'
import './clienteView.css'
import './pipelines.css'
import './dealPanel.css'
import DealOwners from './DealOwners.jsx'
import DealPrimaryLead from './DealPrimaryLead.jsx'
import DealLeadContacts from './DealLeadContacts.jsx'
import DealCompany from './DealCompany.jsx'
import DealAtendimentos from './DealAtendimentos.jsx'
import DealItemsSummary from './DealItemsSummary.jsx'
import DealCommission from './DealCommission.jsx'
import DealOrigin from './DealOrigin.jsx'
import DealPipelinePicker from './DealPipelinePicker.jsx'
import DealTimeline from './DealTimeline.jsx'
import DealDescriptionDialog from './DealDescriptionDialog.jsx'
import DealActionsMenu from './DealActionsMenu.jsx'
import DealDevelopmentDialog from './DealDevelopmentDialog.jsx'
import { stageDurations } from './stageDurations.js'
import DealConfetti from './DealConfetti.jsx'
import DealWonToast from './DealWonToast.jsx'
import useWinBell from './useWinBell.js'

const TABS = [
  { id: 'historico', label: 'Histórico' },
  { id: 'atividades', label: 'Atividades' },
  { id: 'negocios', label: 'Negócios' },
  { id: 'arquivos', label: 'Arquivos' },
  { id: 'atendimentos', label: 'Atendimentos' },
]

const iniciais = (nome) => String(nome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

function OwnerAvatar({ src }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])
  return <span className="dp-owner-avatar" aria-hidden="true">
    {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : <Icon n="user" s={18} />}
  </span>
}

function EmbeddedDetails({ embedded, compact, label, children }) {
  if (!embedded) return children
  return <details className="dp-embedded-details" open={!compact}>
    <summary>{label}<Icon n="chevron_d" s={13} /></summary>
    {children}
  </details>
}

export default function DealPanel({ dealId, initialTab = 'historico', onClose, onEdit, onDeleted, onCreateDeal, onChanged, onOpenChat, onOpenHistoryConversation, embedded = false, compact = false }) {
  const navigate = useNavigate()
  const [developmentPage, setDevelopmentPage] = useState(null)
  const [itemsDiscount, setItemsDiscount] = useState(null)
  const [deal, setDeal] = useState(null)
  const [company, setCompany] = useState(null)

  const [stageHistory, setStageHistory] = useState(null)
  useEffect(() => {
    let alive = true
    setStageHistory(null)
    getDealTransitions(dealId).then((rows) => { if (alive) setStageHistory(rows) }).catch(() => {})
    return () => { alive = false }
  }, [dealId, deal?.stage_id, deal?.status])
  const [celebration, setCelebration] = useState(0)
  const [wonToast, setWonToast] = useState(0)
  const endWonToast = useCallback(() => setWonToast(0), [])
  const playWinBell = useWinBell()
  const celebrate = () => {
    void playWinBell()
    // Comemoração curta e disparada pelo próprio usuário: não respeita prefers-reduced-motion
    // (no Windows com "Efeitos de animação" desligado ele vale sempre e escondia os confetes).
    setCelebration((current) => current + 1)
  }
  const endCelebration = useCallback(() => setCelebration(0), [])
  const [targetStage, setTargetStage] = useState(null)
  const [movingStage, setMovingStage] = useState(false)
  const [moveError, setMoveError] = useState('')
  const confirmStageMove = async () => {
    if (!targetStage || movingStage || targetStage.id === deal?.stage_id) return
    // Etapa de perda na barra de progresso: mesmo tratamento do drag-and-drop
    // no Kanban — pede o motivo antes de mover (LossReasonModal faz o move).
    if (targetStage.is_perdido) { setTargetStage(null); setLossPromptOpen(true); return }
    setMovingStage(true); setMoveError('')
    try {
      const updated = await moveDeal(dealId, { toStageId: targetStage.id })
      setDeal((current) => ({ ...current, ...updated }))
      const wasGanho = targetStage.is_ganho
      setTargetStage(null)
      onChanged?.()
      if (wasGanho) { celebrate(); setWonToast((current) => current + 1); setWinPromptOpen(true) }
    } catch (error) { setMoveError(error.message) }
    finally { setMovingStage(false) }
  }
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [titleSaving, setTitleSaving] = useState(false)
  const [titleError, setTitleError] = useState('')
  const saveTitle = async (event) => {
    event.preventDefault()
    if (titleSaving) return
    const nextTitle = titleDraft.trim()
    if (!nextTitle) { setTitleError('Informe o título do negócio.'); return }
    setTitleSaving(true); setTitleError('')
    try {
      if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
        const { saveDemoDealTitle } = await import('../../demo/data.js')
        saveDemoDealTitle(dealId, nextTitle)
      } else {
        await updateDeal(dealId, { titulo: nextTitle })
      }
      setDeal((current) => ({ ...current, titulo: nextTitle }))
      setEditingTitle(false)
      onChanged?.()
    } catch (error) { setTitleError(error.message) }
    finally { setTitleSaving(false) }
  }
  const [members, setMembers] = useState([])
  const [pipelines, setPipelines] = useState([])
  const [stages, setStages] = useState([])
  const [contacts, setContacts] = useState([])   // leads do deal
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [tab, setTab] = useState(initialTab === 'atividades' ? 'atividades' : 'historico')

  const [historyRevision, setHistoryRevision] = useState(0)
  const [activities, setActivities] = useState([])
  const [noteText, setNoteText] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState(null)
  const [relatedDeals, setRelatedDeals] = useState([]) // negócios do lead principal
  const [tabsLoading, setTabsLoading] = useState(true)
  const [infoDealId, setInfoDealId] = useState(null) // aba Negócios: card aberto → tela de info

  const [descText, setDescText] = useState('')
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const descriptionHeight = useRef(null)
  useEffect(() => { descriptionHeight.current = null; setDescriptionExpanded(false) }, [dealId])
  const descriptionInput = useRef(null)
  useLayoutEffect(() => {
    const input = descriptionInput.current
    if (!input) return
    const resize = () => {
      input.style.height = '0px'
      input.style.overflowY = 'hidden'
      const contentHeight = input.scrollHeight
      input.style.height = `${descriptionHeight.current ?? Math.min(contentHeight, 150)}px`
      input.style.overflowY = 'auto'
    }
    resize()
    let width = input.getBoundingClientRect().width
    const observer = new ResizeObserver(() => {
      const nextWidth = input.getBoundingClientRect().width
      if (nextWidth !== width) { width = nextWidth; resize() }
    })
    observer.observe(input)
    return () => observer.disconnect()
  }, [descText, loading])
  const [descSaving, setDescSaving] = useState(false)
  const [descSaved, setDescSaved] = useState(false)
  const [descError, setDescError] = useState('')

  // ação rápida Ganho/Perdido — sem precisar abrir a edição completa.
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const [lossPromptOpen, setLossPromptOpen] = useState(false)
  const [agendaOpen, setAgendaOpen] = useState(false)
  const [winPromptOpen, setWinPromptOpen] = useState(false)

  useEffect(() => {
    if (embedded) return
    const onKey = (e) => { if (e.key === 'Escape' && !targetStage) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, targetStage, embedded])

  useEffect(() => {
    let alive = true
    getCrmMembers().then((m) => alive && setMembers(m)).catch(() => {})
    getPipelines().then((p) => alive && setPipelines(p)).catch(() => {})
    return () => { alive = false }
  }, [])

  // Carga (dispara ao trocar de deal).
  useEffect(() => {
    if (!dealId) return
    let alive = true
    setLoading(true); setTabsLoading(true); setErr(null); setTab(initialTab === 'atividades' ? 'atividades' : 'historico'); setInfoDealId(null); setCompany(null)

    getDeal(dealId)
      .then((d) => {
        if (!alive) return
        setDeal(d)
        setDescText(d.description || '')
        setDescSaved(false)
        if (d?.pipeline_id) getStages(d.pipeline_id).then((st) => alive && setStages(st)).catch(() => {})
        else setStages([])
        if (d?.company_id) getCompany(d.company_id).then((c) => alive && setCompany(c)).catch(() => alive && setCompany(null))
        else setCompany(null)
      })
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))

    Promise.allSettled([
      getDealActivities(dealId),
      getDealContacts(dealId),
    ]).then(([a, c]) => {
      if (!alive) return
      setActivities(a.status === 'fulfilled' ? a.value : [])
      const cs = c.status === 'fulfilled' ? c.value : []
      setContacts(cs)
      // Negócios relacionados = negócios do lead principal do deal.
      const primary = cs.find((x) => x.is_primary) || cs[0]
      if (primary?.contact_id) {
        getCrmContactDeals(primary.contact_id)
          .then((rd) => alive && setRelatedDeals(rd))
          .catch(() => alive && setRelatedDeals([]))
          .finally(() => alive && setTabsLoading(false))
      } else {
        setRelatedDeals([]); setTabsLoading(false)
      }
    })
    return () => { alive = false }
  }, [dealId])

  const ownerName = useMemo(() => {
    if (!deal?.owner_id) return '—'
    return members.find((m) => m.id === deal.owner_id)?.nome || '—'
  }, [deal, members])
  // Integração pendente: a API de membros atualmente retorna apenas id/nome.
  // avatar_url deve vir do perfil do usuário responsável, não do WhatsApp/lead.
  const owner = members.find((member) => member.id === deal?.owner_id)
  const ownerPhoto = owner?.avatar_url || null

  const stage = useMemo(() => stages.find((s) => s.id === deal?.stage_id) || null, [stages, deal])
  const pipelineNome = useMemo(
    () => pipelines.find((p) => p.id === deal?.pipeline_id)?.nome || null,
    [pipelines, deal],
  )
  const primaryLead = useMemo(() => contacts.find((c) => c.is_primary) || contacts[0] || null, [contacts])
  const ganhoStage = useMemo(() => stages.find((s) => s.is_ganho) || null, [stages])
  const canMarkWon = Boolean(stage?.is_ganho)
  const winDisabledReason = ganhoStage
    ? `Só é possível dar ganho para negócios que estão na etapa ${ganhoStage.nome}.`
    : 'Nenhuma etapa está configurada como ganho neste funil.'

  // ação rápida Ganho/Perdido/Reabrir: move o negócio pela mesma rota real do
  // Kanban (moveDeal). Ganho é imediato; Perdido pede o motivo (LossReasonModal,
  // via confirmLoss abaixo). Reabrir/Tirar ganho não tem etapa-alvo óbvia — volta
  // pra etapa de onde veio (última transição pra etapa atual), ou a primeira
  // etapa do funil se não houver histórico.
  const changeStatus = async (status) => {
    if (statusBusy) return
    if (status === 'ganho') {
      if (!canMarkWon) { setErr(winDisabledReason); return }
      setStatusMenuOpen(false); setStatusBusy(true); setErr(null)
      try {
        const updated = await moveDeal(dealId, { toStageId: ganhoStage.id })
        setDeal((current) => ({ ...current, ...updated }))
        celebrate(); setWonToast((current) => current + 1); setWinPromptOpen(true)
        onChanged?.()
      } catch (error) { setErr(error.message) }
      finally { setStatusBusy(false) }
      return
    }
    if (status === 'perdido') { setStatusMenuOpen(false); setLossPromptOpen(true); return }
    // Perdido marcado pelo botão não troca de etapa → reabrir só volta o status.
    if (deal?.status === 'perdido' && !stage?.is_perdido) {
      setStatusBusy(true); setErr(null)
      try {
        const updated = await updateDeal(dealId, { status: 'aberto' })
        setDeal((current) => ({ ...current, ...updated }))
        onChanged?.()
      } catch (error) { setErr(error.message) }
      finally { setStatusBusy(false) }
      return
    }
    const last = stageHistory?.length
      ? [...stageHistory].sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at))[0]
      : null
    const target = stages.find((s) => s.id === last?.from_stage_id)
      || [...stages].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))[0]
    if (!target) { setErr('Nenhuma etapa disponível para reabrir o negócio.'); return }
    setStatusBusy(true); setErr(null)
    try {
      const updated = await moveDeal(dealId, { toStageId: target.id })
      setDeal((current) => ({ ...current, ...updated }))
      onChanged?.()
    } catch (error) { setErr(error.message) }
    finally { setStatusBusy(false) }
  }

  // Perder NÃO move o negócio: fica na etapa atual, só o status vira "perdido"
  // (PATCH carimba lost_time/data_fechamento e grava o motivo).
  const confirmLoss = async ({ lossReasonId, justificativa }) => {
    const updated = await updateDeal(dealId, { status: 'perdido', loss_reason_id: lossReasonId || null, justificativa: justificativa || null })
    setDeal((d) => ({ ...d, ...updated }))
    onChanged?.()
    setLossPromptOpen(false)
  }

  // Cards da aba Negócios = negócio atual + demais negócios do lead (deduplicado).
  // O atual entra como card mesmo sem lead principal (relatedDeals viria vazio).
  const dealCards = useMemo(() => {
    const map = new Map()
    if (deal) {
      map.set(deal.id, {
        id: deal.id, titulo: deal.titulo, status: deal.status, valor: deal.valor,
        pipeline_nome: pipelineNome, stage_nome: stage?.nome, created_at: deal.created_at,
      })
    }
    for (const d of relatedDeals) map.set(d.id, { ...map.get(d.id), ...d })
    return [...map.values()]
  }, [deal, relatedDeals, pipelineNome, stage])

  // 4 métricas do deal.
  const reloadTabs = async () => {
    setHistoryRevision(n => n + 1)
    const [a] = await Promise.allSettled([getDealActivities(dealId)])
    setActivities(a.status === 'fulfilled' ? a.value : [])
  }

  const saveNote = async () => {
    const text = noteText.trim()
    if (!text || noteSaving) return
    setNoteSaving(true); setNoteError(null)
    try {
      await createDealActivity(dealId, { tipo: 'mensagem', titulo: text })
      setNoteText('')
      await reloadTabs()
    } catch (e) { setNoteError(e.message || 'Não foi possível salvar a nota.') }
    finally { setNoteSaving(false) }
  }

  const saveDescricao = async () => {
    if (descSaving || descText === (deal?.description || '')) return
    setDescSaving(true); setDescError('')
    try {
      await updateDeal(dealId, { description: descText || null })
      setDeal((d) => ({ ...d, description: descText }))
      setDescSaved(true)
    } catch (e) { setDescError(e.message) } finally { setDescSaving(false) }
  }

  const [archiving, setArchiving] = useState(false)
  const handleArchive = async () => {
    if (!deal || archiving) return
    setArchiving(true); setErr(null)
    try {
      await updateDeal(dealId, { archived: true })
      onClose()
    } catch (e) { setErr(e.message); setArchiving(false) }
  }

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!deal) return
    setDeleting(true); setErr(null)
    try { await deleteDeal(dealId); onDeleted() }
    catch (e) { setErr(e.message); setDeleting(false); setDeleteOpen(false) }
  }

  // Exclusão de um negócio a partir da aba "Negócios" → só na tela de detalhes
  // (DealInfoScreen). Se for o próprio deal aberto, fecha o painel (onDeleted);
  // se for outro negócio do lead, remove da lista e volta p/ a lista de negócios.
  const handleInfoDeleted = (deletedId) => {
    if (deletedId === dealId) { onDeleted(); return }
    setRelatedDeals((prev) => prev.filter((d) => d.id !== deletedId))
    setInfoDealId(null)
  }

  // Desvincular um negócio DESTE lead (apaga só o deal_contacts do lead
  // principal). O negócio permanece na pipeline — resolve a duplicidade: manter
  // ambos os deals, mas o lead ligado só ao que o usuário quer. Nunca deleta o deal.
  const [dealToUnlink, setDealToUnlink] = useState(null)
  const [unlinking, setUnlinking] = useState(false)

  const reloadRelated = async () => {
    if (!primaryLead?.contact_id) { setRelatedDeals([]); return }
    try { setRelatedDeals(await getCrmContactDeals(primaryLead.contact_id)) }
    catch { setRelatedDeals([]) }
  }

  const confirmUnlink = async () => {
    if (!dealToUnlink || !primaryLead?.contact_id) return
    setUnlinking(true); setErr(null)
    try {
      await unlinkDealContact(dealToUnlink.id, primaryLead.contact_id)
      await reloadRelated()
      setDealToUnlink(null)
    } catch (e) { setErr(e.message) } finally { setUnlinking(false) }
  }

  const titulo = deal?.titulo || 'Negócio'
  const orderedStages = [...stages].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
  const currentStageIndex = orderedStages.findIndex((item) => item.id === deal?.stage_id)
  const daysByStage = stageHistory ? stageDurations(deal, stageHistory) : {}
  const statusColor = DEAL_STATUS_COLORS[deal?.status] || '#667085'
  const descDirty = descText !== (deal?.description || '')

  return (
    <>
      {developmentPage && <DealDevelopmentDialog page={developmentPage} onClose={() => setDevelopmentPage(null)} />}
      {celebration > 0 && <DealConfetti key={celebration} onEnd={endCelebration} />}
      {wonToast > 0 && <DealWonToast key={wonToast} onEnd={endWonToast} />}
      <aside className={`pm-dp-drawer${embedded ? ' dp-embedded' : ''}${compact ? ' dp-compact' : ''}`} aria-label={embedded ? 'Detalhes do negócio' : undefined} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-drawer-head">
          {!embedded && <span className="dp-heading">
            Painel do negócio
          </span>}
          {deal && !loading && <div className="dp-status-controls">
            <span className="dp-status-label" role="status">Status: {({ aberto: 'Aberto', ganho: 'Ganho', perdido: 'Perdido' })[deal.status] || deal.status}</span>
            {deal.status === 'aberto' ? <>
              <div className={`dp-status-split${!canMarkWon ? ' is-disabled' : ''}`} title={!canMarkWon ? winDisabledReason : undefined}>
                <button type="button" className="dp-status-won" title={!canMarkWon ? winDisabledReason : undefined} disabled={statusBusy || !canMarkWon} onClick={() => changeStatus('ganho')}>Ganho</button>
              </div>
              <button type="button" className="dp-status-lost" disabled={statusBusy} onClick={() => changeStatus('perdido')}>Perdido</button>
            </> : <button type="button" disabled={statusBusy} onClick={() => changeStatus('aberto')}>{deal.status === 'ganho' ? 'Tirar ganho' : 'Reabrir'}</button>}
            <DealActionsMenu onArchive={handleArchive} onDelete={() => setDeleteOpen(true)} />
          </div>}
          {!embedded && <button className="fc-drawer-x" onClick={onClose} title="Fechar painel (Esc)">
            <Icon n="x" s={18} sw={2} />
          </button>}
        </div>

        {err && <div className="fc-error" style={{ margin: '10px 20px 0' }}>{err}</div>}

        {loading ? (
          <div className="cv-muted">Carregando…</div>
        ) : !deal ? (
          <div className="cv-muted">Negócio não encontrado.</div>
        ) : (
          <div key={dealId} className="cv-wrap pm-dp-wrap">
            {orderedStages.length > 0 && <div className="dp-stage-progress" aria-label={`Etapas do funil. Etapa atual: ${stage?.nome || 'Não definida'}`}>
              <ol className="dp-stage-track">
                {orderedStages.map((item, index) => <li key={item.id} className={`dp-stage-segment${currentStageIndex >= 0 && index <= currentStageIndex ? ' is-filled' : ''}${index === currentStageIndex ? ' is-current' : ''}`} aria-current={index === currentStageIndex ? 'step' : undefined} title={`${item.nome}${index === currentStageIndex ? ' · Etapa atual' : ''}`}>
                  <button type="button" disabled={index === currentStageIndex || movingStage} onClick={() => { setMoveError(''); setTargetStage(item) }} aria-label={index === currentStageIndex ? `Etapa atual: ${item.nome}` : `Mover negócio para ${item.nome}`}>{item.nome}{daysByStage[item.id] != null && <span className="dp-stage-days" title="Tempo total nesta etapa">{daysByStage[item.id]} {daysByStage[item.id] === 1 ? 'dia' : 'dias'}</span>}</button>
                </li>)}
              </ol>
            </div>}
            {/* ═══ COLUNA ESQUERDA ══════════════════════════════════════ */}
            <div className="cv-card cv-left">
              <div className="cv-identity">
                <div style={{ minWidth: 0, flex: 1 }}>
                  {editingTitle ? <form className="dp-title-editor" onSubmit={saveTitle} onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); if (!titleSaving) setEditingTitle(false) } }}>
                    <input autoFocus aria-label="Título do negócio" value={titleDraft} maxLength={200} disabled={titleSaving} onChange={(event) => setTitleDraft(event.target.value)} />
                    {titleError && <small role="alert">{titleError}</small>}
                    <div><button type="submit" className="cv-btn cv-btn-primary" disabled={titleSaving || !titleDraft.trim()}>{titleSaving ? 'Salvando…' : 'Salvar'}</button><button type="button" className="cv-btn" disabled={titleSaving} onClick={() => setEditingTitle(false)}>Cancelar</button></div>
                  </form> : <button type="button" className="cv-name dp-title-button" title="Editar título do negócio" onClick={() => { setTitleDraft(deal.titulo || ''); setTitleError(''); setEditingTitle(true) }}>{titulo}</button>}
                </div>
                {embedded && <a className="dp-open-commercial" href={`/comercial/pipelines?deal=${encodeURIComponent(dealId)}`} target="_blank" rel="noopener noreferrer" aria-label="Abrir negócio no Comercial em nova aba" title="Abrir negócio no Comercial em nova aba"><Icon n="external" s={16} /></a>}
              </div>

              <EmbeddedDetails embedded={embedded} compact={compact} label="Informações do negócio">
              <div className="cv-rows">
                <DealPipelinePicker key={dealId} deal={deal} pipelines={pipelines} currentName={pipelineNome} onMoved={(updated, nextStages) => {
                  setDeal(current => ({ ...current, ...updated }))
                  setStages(nextStages)
                  reloadTabs()
                  reloadRelated()
                  onChanged?.()
                }} />
              <DealOrigin dealId={dealId} lead={primaryLead} onSaved={(changes) => { setContacts((current) => current.map((item) => item.contact_id === primaryLead?.contact_id ? { ...item, ...changes } : item)); onChanged?.() }} />
                <DealOwners onOpenDetails={() => setDevelopmentPage('responsavel')} deal={deal} members={members} Avatar={OwnerAvatar} onSaved={(changes) => { setDeal((current) => ({ ...current, ...changes })); onChanged?.() }} />
                <DealPrimaryLead onOpenDetails={(lead) => { const id = lead?.contact_id || lead?.id; if (id) navigate(`/comercial/leads/${encodeURIComponent(id)}`) }} dealId={dealId} lead={primaryLead} Avatar={OwnerAvatar} onSaved={(contact, { removedContactId } = {}) => {
                  setContacts((current) => [contact, ...current.filter((item) => item.contact_id !== contact.contact_id && item.contact_id !== removedContactId).map((item) => ({ ...item, is_primary: false }))])
                  setRelatedDeals([])
                  getCrmContactDeals(contact.contact_id).then(setRelatedDeals).catch(() => {})
                  onChanged?.()
                }} />
                {primaryLead && <DealLeadContacts key={primaryLead.contact_id || primaryLead.id} lead={primaryLead} onChannelsSaved={(changes) => { setContacts((current) => current.map((item) => item.contact_id === primaryLead.contact_id ? { ...item, ...changes } : item)); onChanged?.() }} onOpenChat={onOpenChat ? (phone) => onOpenChat({ ...deal, primary_lead: primaryLead.nome, chatContactId: primaryLead.contact_id || primaryLead.id, chatPhone: phone }) : undefined} />}
                <DealCompany onOpenDetails={() => { if (company?.id) navigate(`/comercial/empresas/${encodeURIComponent(company.id)}`) }} dealId={dealId} company={company} onSaved={(saved) => { setCompany(saved); setDeal((current) => ({ ...current, company_id: saved.id })); onChanged?.() }} />
              </div>


              <div className="cv-divider" />

              <div className="cv-label">Resumo</div>
              <DealItemsSummary dealId={dealId} onDiscountChange={setItemsDiscount} onValueSaved={(valor) => { setDeal((current) => ({ ...current, valor })); onChanged?.() }} />
              <div className="cv-data-row">
                <span className="cv-data-label">Total do negócio</span>
                <span className="cv-data-value">{deal.valor != null ? fmtBRL(deal.valor) : '—'}</span>
              </div>
              {itemsDiscount?.dealId === dealId && itemsDiscount.amount > 0 && <div className="cv-data-row dp-deal-discount">
                <span className="cv-data-label">Desconto</span>
                <span className="cv-data-value">{fmtBRL(itemsDiscount.amount)} · {itemsDiscount.percent.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>
              </div>}
              <DealCommission key={`${dealId}:${deal.comissao_percentual ?? ''}`} dealId={dealId} total={deal.valor} percent={deal.comissao_percentual} onSaved={(updated) => { setDeal((current) => ({ ...current, ...updated })); onChanged?.() }} />
              <div className="cv-divider" />
              </EmbeddedDetails>

            </div>

            {/* ═══ COLUNA DIREITA ═══════════════════════════════════════ */}
            <div className="cv-card cv-right">
              <EmbeddedDetails embedded={embedded} compact={compact} label="Descrição">
              <div className="cv-note-box dp-description">
                <div className="cv-note-head">
                  <span className="cv-note-title">Detalhes</span>
                  <button type="button" className="dp-description-expand" onClick={() => setDescriptionExpanded(true)}>Expandir descrição</button>
                  {descSaved && !descDirty && <span style={{ fontSize: 11, fontWeight: 700, color: '#17a34a' }}>salvo ✓</span>}
                </div>
                <textarea
                  className="cv-note-textarea"
                  ref={descriptionInput}
                  onPointerUp={event => { descriptionHeight.current = event.currentTarget.getBoundingClientRect().height }}
                  rows={1}
                  aria-label="Detalhes do negócio"
                  placeholder="Anotações sobre este negócio…"
                  value={descText}
                  onChange={(e) => { setDescText(e.target.value); setDescSaved(false) }}
                />
                {(descDirty || descSaving) && <div className="cv-note-foot">
                  <button className="cv-btn cv-btn-primary" onClick={saveDescricao} disabled={!descDirty || descSaving}>
                    {descSaving ? 'Salvando…' : 'Salvar'}
                  </button>
                </div>}
                {descError && <p role="alert" className="fc-error">{descError}</p>}
                {descriptionExpanded && <DealDescriptionDialog value={descText} onChange={value => { setDescText(value); setDescSaved(false) }} onClose={() => setDescriptionExpanded(false)} onSave={saveDescricao} saving={descSaving} dirty={descDirty} saved={descSaved} error={descError} />}
              </div>
              </EmbeddedDetails>
              <div className="cv-tabs">
                <div className="cv-tabs-list">
                  {TABS.filter((t) => !embedded || t.id !== 'atendimentos').map((t) => (
                    <button key={t.id} type="button" className={`cv-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
                      {t.label}
                      {t.id === 'atividades' && activities.some(a => a.tipo !== 'mensagem') && <span className="cv-tab-count">{activities.filter(a => a.tipo !== 'mensagem').length}</span>}
                      {t.id === 'negocios' && dealCards.length > 0 && <span className="cv-tab-count">{dealCards.length}</span>}
                    </button>
                  ))}
                </div>
                <button type="button" className="cv-tabs-close" onClick={onClose} title="Fechar painel (Esc)">
                  <Icon n="x" s={16} sw={2} />
                </button>
              </div>

              <div className="cv-tabbody">
                {tab === 'historico' && <div className="cv-note-box" style={{ marginBottom: 12 }}>
                  <div className="cv-note-head"><span className="cv-note-title">Nova nota</span></div>
                  <textarea className="cv-note-textarea" rows={2} aria-label="Nota do negócio" maxLength={10000} placeholder="Escreva uma nota sobre este negócio…" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                  {noteText.trim() && <div className="cv-note-foot">
                    <button className="cv-btn cv-btn-primary" onClick={saveNote} disabled={noteSaving}>{noteSaving ? 'Salvando…' : 'Adicionar nota'}</button>
                  </div>}
                  {noteError && <p role="alert" className="fc-error">{noteError}</p>}
                </div>}
                {tab === 'historico' && <DealTimeline dealId={dealId} unified={!embedded} showEventActions={embedded} onOpenConversation={onOpenHistoryConversation} onChanged={reloadTabs} revision={`${historyRevision}:${deal?.stage_id}:${deal?.status}`} relationshipRevision={`${primaryLead?.contact_id}:${company?.id}`} />}
                {tab === 'atividades' && (
                  <ActivitiesTab
                    dealDesign
                    items={activities.filter(a => a.tipo !== 'mensagem')} loading={tabsLoading} members={members}
                    createActivity={(payload) => createDealActivity(dealId, payload)}
                    onCreated={reloadTabs}
                  />
                )}
                {tab === 'negocios' && (
                  infoDealId ? (
                    <DealInfoScreen
                      dealId={infoDealId} members={members}
                      onBack={() => setInfoDealId(null)}
                      onDeleted={handleInfoDeleted}
                    />
                  ) : (
                    <DealsTab
                      items={dealCards} loading={tabsLoading} onOpen={setInfoDealId}
                      onCreate={onCreateDeal ? () => onCreateDeal(primaryLead) : undefined}
                      onRemove={primaryLead ? setDealToUnlink : undefined}
                      hideRemoveId={dealId}
                      removeTitle="Desvincular deste lead"
                      createLabel="Adicionar negócio"
                      emptyMsg="Nenhum negócio vinculado a este lead."
                    />
                  )
                )}
                {tab === 'arquivos' && <FilesTab dealId={dealId} />}
                {!embedded && tab === 'atendimentos' && <DealAtendimentos contactId={primaryLead?.contact_id} dealId={dealId} members={members} />}
              </div>
            </div>
          </div>
        )}
      </aside>

      <ConfirmDialog
        open={!!targetStage}
        title="Mover negócio"
        message={<><span>Mover negócio para a etapa “{targetStage?.nome}”?</span>{moveError && <p role="alert" style={{ color: '#b42318' }}>{moveError}</p>}</>}
        confirmLabel="Mover negócio"
        busy={movingStage}
        onConfirm={confirmStageMove}
        onCancel={() => { if (!movingStage) setTargetStage(null) }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Excluir negócio"
        message={`Apagar o negócio "${deal?.titulo || 'Sem título'}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => { if (!deleting) setDeleteOpen(false) }}
      />

      <ConfirmDialog
        open={!!dealToUnlink}
        title="Desvincular negócio"
        message={dealToUnlink
          ? `Desvincular o negócio "${dealToUnlink.titulo || 'Negócio'}" deste lead? O negócio continua na pipeline — só o vínculo com este lead é removido.`
          : ''}
        confirmLabel="Desvincular"
        danger
        busy={unlinking}
        onConfirm={confirmUnlink}
        onCancel={() => { if (!unlinking) setDealToUnlink(null) }}
      />

      {lossPromptOpen && (
        <LossReasonModal
          onCancel={() => setLossPromptOpen(false)}
          onConfirm={confirmLoss}
        />
      )}

      {winPromptOpen && deal && (
        <DealWinFlow
          deal={deal}
          onClose={() => setWinPromptOpen(false)}
          onClientConverted={() => { onChanged?.(); getDealContacts(dealId).then(setContacts).catch(() => {}) }}
          onProjectCreated={(proj) => { setWinPromptOpen(false); navigate(`/projetos/${proj.id}`) }}
        />
      )}

      {agendaOpen && deal && (
        <AgendaEventModal
          preset={{ deal_id: dealId, deal_titulo: deal.titulo || 'Negócio' }}
          members={members}
          onClose={() => setAgendaOpen(false)}
          onSaved={() => { setAgendaOpen(false); reloadTabs() }}
        />
      )}
    </>
  )
}

// ── Tela "Informações do Negócio" — detalhes completos de UM negócio ────────
// Aberta ao clicar num card da aba Negócios. Self-fetch por dealId (serve o
// negócio atual E outros negócios do lead). Reúne tudo o que antes ficava solto
// na aba Negócios: dados, descrição, justificativa, itens e contrato.
function DealInfoScreen({ dealId, members, onBack, onDeleted }) {
  const [deal, setDeal] = useState(null)

  const [stages, setStages] = useState([])
  const [pipelines, setPipelines] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const remove = async () => {
    setDeleting(true); setErr(null)
    try { await deleteDeal(dealId); onDeleted?.(dealId) }
    catch (e) { setErr(e.message); setDeleting(false); setConfirmOpen(false) }
  }

  useEffect(() => {
    let alive = true
    setLoading(true); setErr(null)
    getPipelines().then((p) => alive && setPipelines(p)).catch(() => {})
    getDeal(dealId)
      .then((d) => {
        if (!alive) return
        setDeal(d)
        if (d?.pipeline_id) getStages(d.pipeline_id).then((st) => alive && setStages(st)).catch(() => {})
        else setStages([])
      })
      .catch((e) => alive && setErr(e.message))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [dealId])

  const stage = useMemo(() => stages.find((s) => s.id === deal?.stage_id) || null, [stages, deal])
  const pipelineNome = useMemo(() => pipelines.find((p) => p.id === deal?.pipeline_id)?.nome || null, [pipelines, deal])
  const ownerName = useMemo(() => {
    if (!deal?.owner_id) return '—'
    return members.find((m) => m.id === deal.owner_id)?.nome || '—'
  }, [deal, members])

  const statusColor = DEAL_STATUS_COLORS[deal?.status] || '#667085'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <button className="cv-editfields" onClick={onBack} style={{ marginTop: 0 }}>
          <Icon n="chevron_l" s={16} /> Negócios
        </button>
        {deal && (
          <button className="pm-btn-danger" onClick={() => setConfirmOpen(true)} title="Excluir negócio">
            <Icon n="trash" s={14} /> Excluir
          </button>
        )}
      </div>

      {err && <div className="fc-error" style={{ marginTop: 8 }}>{err}</div>}
      {loading ? (
        <div className="cv-muted">Carregando…</div>
      ) : !deal ? (
        <div className="cv-muted">Negócio não encontrado.</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#101828', minWidth: 0 }}>{deal.titulo || 'Negócio'}</div>
            {deal.status && (
              <span className="cv-pill" style={{ background: `${statusColor}1a`, borderColor: `${statusColor}55`, color: statusColor }}>
                {deal.status}
              </span>
            )}
          </div>

          <div className="cv-label">Dados do negócio</div>
          <div className="cv-data-row">
            <span className="cv-data-label">Pipeline</span>
            <span className="cv-data-value">{pipelineNome || '—'}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Etapa atual</span>
            <span className="cv-data-value">{stage?.nome || '—'}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Valor</span>
            <span className="cv-data-value">{deal.valor != null ? fmtBRL(deal.valor) : '—'}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Probabilidade</span>
            <span className="cv-data-value">{stage?.probabilidade != null ? `${stage.probabilidade}%` : '—'}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Responsável</span>
            <span className="cv-data-value">{ownerName}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Status</span>
            <span className="cv-data-value">{deal.status}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Data de criação</span>
            <span className="cv-data-value">{fmtDateTime(deal.created_at)}</span>
          </div>
          <div className="cv-data-row">
            <span className="cv-data-label">Última atualização</span>
            <span className="cv-data-value">{fmtDateTime(deal.updated_at)}</span>
          </div>
          {deal.data_fechamento && (
            <div className="cv-data-row">
              <span className="cv-data-label">Data de fechamento</span>
              <span className="cv-data-value">{fmtDateTime(deal.data_fechamento)}</span>
            </div>
          )}

          {deal.description && (
            <div className="pm-section">
              <div className="pm-section-title" style={{ marginBottom: 8 }}>Descrição</div>
              <div style={{ fontSize: 13.5, color: '#344054', lineHeight: 1.5 }}>{deal.description}</div>
            </div>
          )}

          {deal.justificativa && (
            <div className="pm-section">
              <div className="pm-section-title" style={{ marginBottom: 8 }}>Justificativa</div>
              <div style={{ fontSize: 13.5, color: '#344054', lineHeight: 1.5 }}>{deal.justificativa}</div>
            </div>
          )}

          <div className="pm-section">
            <div className="pm-section-title" style={{ marginBottom: 10 }}>Itens do negócio</div>
            <ItemsTab dealId={dealId} />
          </div>

          <div className="pm-section">
            <div className="pm-section-title" style={{ marginBottom: 10 }}>Contrato</div>
            <ContractsTab dealId={dealId} deal={deal} />
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Excluir negócio"
        message={`Excluir o negócio "${deal?.titulo || 'Sem título'}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        danger
        busy={deleting}
        onConfirm={remove}
        onCancel={() => { if (!deleting) setConfirmOpen(false) }}
      />
    </div>
  )
}
